import * as React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import classnames from 'classnames';
import isNil from 'lodash/isNil';
import snakeCase from 'lodash/snakeCase';

import Popover from 'components/popover/Popover';

import { __t, applyTranslations } from 'utils/language';
import { DELIVERABLE_TYPES, sortNewDeliverables } from 'utils/deliverables';
import { SUBCAMPAIGN_OFFER_SORT_VALUES } from 'utils/subcampaign';
import { isBrandsFocusedOrganization, isPlainInfluencerOrganization } from 'utils/organization';
import { HTML_DATE_FORMAT, getDisplayDate } from 'utils/date';

import './overdue-deliverables-popover.scss';

applyTranslations(require('./overdue-deliverables-popover.i18n.json'));

type TitleCounts = {
  [x: string]: number
};

type ContentItemType = 'contentApproval' | 'contentGoLive' | 'insightsProvision';

import type {
  Deliverable,
  DeliverableOverdueEndpoint,
  DeliverableType,
  DeliverablesOverdueLocal,
  Organization
} from 'types';

type OwnProps = {
  overdueDeliverables: DeliverablesOverdueLocal | null,
  deliverables?: Array<Partial<Deliverable>>,
  deliverableType?: DeliverableType,
  subcampaignOfferSortState?: string,
  triggerComponent: React.ReactNode,
  classNameWrapper?: string,
  classNamePopover?: string
};

type StateProps = {
  currentOrganization: Organization
};

type Props = OwnProps & StateProps;

const MSG_KEY = 'overdue_deliverables_popover';

class OverdueDeliverablesPopover extends React.PureComponent<Props> {
  static defaultProps = {
    deliverables: [],
    classNamePopover: 'overdue-deliverables-popover__popover',
    classNameWrapper: 'overdue-deliverables-popover__wrapper'
  };

  private renderContentItem = (
    deliverable: DeliverableOverdueEndpoint,
    type: ContentItemType
  ): React.ReactNode => {
    const { currentOrganization } = this.props;
    const timelineKey = `${type}Timeline`;
    const startDate = deliverable[timelineKey]?.startDate;
    const endDate = deliverable[timelineKey]?.endDate;
    const status = deliverable[timelineKey]?.status;

    if (isNil(startDate) && isNil(endDate)) {
      return null;
    }

    const isInfluencer = isPlainInfluencerOrganization(currentOrganization);
    const isBrand = isBrandsFocusedOrganization(currentOrganization);
    const startDateMoment = moment(startDate, HTML_DATE_FORMAT);
    const endDateMoment = moment(endDate, HTML_DATE_FORMAT);
    const datesAreEqual = startDateMoment.isSame(endDateMoment);
    const isInfluencerOverdue = status === 'overdue';
    const isBrandOverdue = status === 'brand_overdue' ||
      (['contentApprovalTimeline', 'insightsProvisionTimeline'].includes(timelineKey) && status === 'overdue');

    const containerClasses = classnames('overdue-deliverables-popover__item', {
      'overdue-deliverables-popover__item--influencer-overdue': isInfluencer && isInfluencerOverdue,
      'overdue-deliverables-popover__item--brand-overdue': isBrand && isBrandOverdue,
      'overdue-deliverables-popover__item--brand-review': isInfluencer && status === 'brand_overdue'
    });

    return (
      <div className={containerClasses}>
        <div className="overdue-deliverables-popover__description">
          {__t(`${MSG_KEY}.${snakeCase(type)}`)}
        </div>
        <div className="overdue-deliverables-popover__range">
          {`${getDisplayDate(startDateMoment)}${!datesAreEqual ? ` - ${getDisplayDate(endDateMoment)}` : ''}`}
        </div>
        {
          status && (
            <div className="overdue-deliverables-popover__overdue">
              {__t(`${MSG_KEY}.${status}.${isInfluencer ? 'influencer' : 'brand'}`)}
            </div>
          )
        }
      </div>
    );
  };

  private renderContentDeliverable = (
    deliverable: DeliverableOverdueEndpoint,
    counts: TitleCounts
  ): React.ReactNode => {
    const defaultDeliverableTitles = DELIVERABLE_TYPES.map(type => (
      type.replace(/_/, ' ').toLowerCase()
    ));

    const deliverableName = deliverable.name?.toLowerCase() || '';
    const index = defaultDeliverableTitles.includes(deliverableName)
      ? ((counts[deliverableName] ?? 0) + 1)
      : 0;

    // eslint-disable-next-line no-param-reassign
    counts[deliverableName] = index;

    return (
      <React.Fragment key={`overdue-deliverables-popover--${deliverable.id}`}>
        <div className="overdue-deliverables-popover__name">
          {`${deliverable.name}${counts[deliverableName] > 0 ? ` #${counts[deliverableName]}` : ''}`}
        </div>
        <div className="overdue-deliverables-popover__items">
          {this.renderContentItem(deliverable, 'contentApproval')}
          {this.renderContentItem(deliverable, 'contentGoLive')}
          {this.renderContentItem(deliverable, 'insightsProvision')}
        </div>
      </React.Fragment>
    );
  };

  private renderContent(): React.ReactNode {
    const { deliverables: offerDeliverables, overdueDeliverables, deliverableType, subcampaignOfferSortState } = this.props;

    if (isNil(overdueDeliverables)) {
      return null;
    }

    const { deliverables } = overdueDeliverables;
    const deliverablesCopy = [...deliverables]; // prevent state mutation invariant violation
    const counts = {};

    let allDeliverables = (offerDeliverables || []).reduce((acc, item) => {
      const foundDeliverable = deliverablesCopy.find(deliverable => deliverable.id === item.id);

      if (isNil(foundDeliverable)) {
        acc.push(item);
      }

      return acc;
    }, deliverablesCopy as Array<Partial<Deliverable>>);

    allDeliverables = sortNewDeliverables(allDeliverables, subcampaignOfferSortState || SUBCAMPAIGN_OFFER_SORT_VALUES[0]);

    const deliverablesPerType = allDeliverables.filter(deliverable => deliverable.deliverableType === deliverableType);
    const popoverDeliverables = isNil(deliverableType) ? allDeliverables : deliverablesPerType;

    return (
      <div className="overdue-deliverables-popover__content">
        {popoverDeliverables.map(deliverable => (
          this.renderContentDeliverable(deliverable as DeliverableOverdueEndpoint, counts)
        ))}
      </div>
    );
  }

  render(): React.ReactNode {
    const { overdueDeliverables, triggerComponent, classNameWrapper, classNamePopover } = this.props;

    if (isNil(overdueDeliverables)) {
      return null;
    }

    return (
      <div className="overdue-deliverables-popover">
        <Popover
          classNameWrapper={classNameWrapper}
          classNamePopover={classNamePopover}
          triggerComponent={triggerComponent}
        >
          {this.renderContent()}
        </Popover>
      </div>
    );
  }
}

const mapStateToProps = ({ organizations }): StateProps => ({
  currentOrganization: organizations.currentOrganization
});

export default connect(mapStateToProps)(OverdueDeliverablesPopover);
